import React, { useState, useEffect } from 'react';
import { Modal, Button, Table, message, Upload, Tooltip } from 'antd';
import { DownloadOutlined, UploadOutlined, ReloadOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useLazyQuery } from '@apollo/client';
import type { ColumnsType } from 'antd/es/table';
import * as XLSX from 'xlsx';
import {
  GET_CLIENT_DETAIL_ADJUSTMENT_TASKS,
  GET_CLIENT_DETAIL_ADJUSTMENT_RESULT_DOWNLOAD_URL,
  type ClientDetailAdjustmentTaskType,
  type GetClientDetailAdjustmentTasksResponse,
} from '../../graphql/queries/settlement/clientDetailAdjustmentQueries';
import { SUBMIT_CLIENT_DETAIL_ADJUSTMENT } from '../../graphql/mutations/settlement/clientDetailAdjustmentMutations';
import {HasResource} from "../HasResource";
import {ResourceCode} from "../../constants/resourceCodes";

const TEMPLATE_NAME = '客户明细调账模板.xlsx';
const TEMPLATE_HEADERS = ['业务订单号', '基础运费', '超重费', '超长费'];
const UPLOAD_PARSE_MESSAGE_KEY = 'client-detail-adjustment-upload';

/** 与 SettlementConstants.JobResultStatus.COMPLETED 一致，结果文件已生成并可下载 */
const CLIENT_DETAIL_ADJUSTMENT_RESULT_READY = 'COMPLETED';

interface ClientDetailAdjustmentModalProps {
  visible: boolean;
  onCancel: () => void;
  billId: string;
  billInfo?: { clientName?: string; billAmount?: string };
  onSuccess?: () => void;
}

export const ClientDetailAdjustmentModal: React.FC<ClientDetailAdjustmentModalProps> = ({
  visible,
  onCancel,
  billId,
  billInfo,
  onSuccess,
}) => {
  const [uploading, setUploading] = useState(false);

  const { data, loading, refetch } = useQuery<GetClientDetailAdjustmentTasksResponse>(
    GET_CLIENT_DETAIL_ADJUSTMENT_TASKS,
    {
      variables: { billId },
      skip: !billId || !visible,
      fetchPolicy: 'cache-and-network',
    }
  );
  const [submitClientDetailAdjustment] = useMutation(SUBMIT_CLIENT_DETAIL_ADJUSTMENT);
  const [getResultDownloadUrl] = useLazyQuery(GET_CLIENT_DETAIL_ADJUSTMENT_RESULT_DOWNLOAD_URL, {
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (visible && billId) {
      refetch();
    }
  }, [visible, billId, refetch]);

  const handleDownloadTemplate = () => {
    const worksheetData = [TEMPLATE_HEADERS];
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', TEMPLATE_NAME);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    message.success('模板下载成功');
  };

  const readFileAsBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const dataUrl = reader.result;
        if (typeof dataUrl !== 'string') {
          reject(new Error('读取文件失败'));
          return;
        }
        const comma = dataUrl.indexOf(',');
        if (comma === -1) {
          reject(new Error('读取文件失败'));
          return;
        }
        resolve(dataUrl.slice(comma + 1));
      };
      reader.onerror = () => reject(new Error('读取文件失败'));
      reader.readAsDataURL(file);
    });

  const handleUpload = async (file: File) => {
    const isExcel = file.name.endsWith('.xlsx') || file.name.endsWith('.xls');
    if (!isExcel) {
      message.error('请上传 Excel 文件（.xlsx 或 .xls）');
      return false;
    }

    setUploading(true);
    message.loading({
      content:
        '正在上传文件到服务端（数据行多或文件较大时时间较长，请勿关闭页面）…',
      key: UPLOAD_PARSE_MESSAGE_KEY,
      duration: 0,
    });
    try {
      const base64 = await readFileAsBase64(file);

      const result = await submitClientDetailAdjustment({
        variables: { billId, fileBase64: base64 },
      });

      const resp = result.data?.submitClientDetailAdjustment;
      if (resp?.success) {
        message.destroy(UPLOAD_PARSE_MESSAGE_KEY);
        message.success('明细已提交，系统将异步处理');
        refetch();
        onSuccess?.();
      } else {
        message.destroy(UPLOAD_PARSE_MESSAGE_KEY);
        message.error(resp?.errorMessage || '提交失败');
      }
    } catch (error: unknown) {
      message.destroy(UPLOAD_PARSE_MESSAGE_KEY);
      const err = error as Error;
      message.error('上传失败: ' + (err?.message || '未知错误'));
    } finally {
      setUploading(false);
    }
    return false;
  };

  const isClientDetailAdjustmentResultReady = (record: ClientDetailAdjustmentTaskType) =>
    record.parseStatus === CLIENT_DETAIL_ADJUSTMENT_RESULT_READY;

  const handleDownloadResult = async (record: ClientDetailAdjustmentTaskType) => {
    if (!isClientDetailAdjustmentResultReady(record)) {
      message.warning('结果文件生成完成后才可下载');
      return;
    }
    if (record.resultFileUrl) {
      window.open(record.resultFileUrl, '_blank');
      message.success('开始下载');
      return;
    }
    try {
      const res = await getResultDownloadUrl({ variables: { taskId: record.id } });
      const url = res.data?.getClientDetailAdjustmentResultDownloadUrl;
      if (url) {
        window.open(url, '_blank');
        message.success('开始下载');
      } else {
        message.warning('结果文件尚未生成');
      }
    } catch {
      message.warning('结果文件尚未生成');
    }
  };

  const columns: ColumnsType<ClientDetailAdjustmentTaskType> = [
    { title: '调账任务ID', dataIndex: 'id', key: 'id', width: 180 },
    {
      title: '任务创建时间 (UTC)',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 220,
      ellipsis: true,
    },
    {
      title: '任务执行完成时间 (UTC)',
      dataIndex: 'completedAt',
      key: 'completedAt',
      width: 220,
      ellipsis: true,
      render: (v: string | null | undefined) => v ?? '—',
    },
    { title: '任务提交人', dataIndex: 'submitterName', key: 'submitterName', width: 120 },
    { title: '订单数量', dataIndex: 'orderCount', key: 'orderCount', width: 100, align: 'center' },
    {
      title: '成功数',
      dataIndex: 'successCount',
      key: 'successCount',
      width: 100,
      align: 'center',
      render: (c: number) => <span className="text-green-600">{c}</span>,
    },
    {
      title: '失败数',
      dataIndex: 'failCount',
      key: 'failCount',
      width: 100,
      align: 'center',
      render: (c: number) => <span className={c > 0 ? 'text-red-600' : ''}>{c}</span>,
    },
    { title: '解析状态', dataIndex: 'parseStatus', key: 'parseStatus', width: 120 },
    {
      title: '任务成功失败明细',
      key: 'download',
      width: 140,
      render: (_, record) => {
        const ready = isClientDetailAdjustmentResultReady(record);
        return (
            <HasResource resourceCodes={[ResourceCode.settlement.SETTLEMENT_REVENUE_BILL_OPERATE]}>
              <Tooltip title={ready ? undefined : '结果文件生成完成后可下载'}>
                <span>
                  <Button
                      type="link"
                      size="small"
                      icon={<DownloadOutlined/>}
                      disabled={!ready}
                      onClick={() => handleDownloadResult(record)}
                  >
                    下载
                  </Button>
                </span>
              </Tooltip>
            </HasResource>
        );
      },
    },
  ];

  const tasks = data?.getClientDetailAdjustmentTasks ?? [];

  return (
    <Modal
      title="明细调账"
      open={visible}
      onCancel={onCancel}
      width={900}
      footer={null}
      destroyOnClose
    >
      {billInfo?.clientName && (
        <p className="mb-4 text-gray-600">
          账单：{billInfo.clientName}
          {billInfo.billAmount != null ? ` · 金额 ${billInfo.billAmount}` : ''}
        </p>
      )}
      <div className="mb-4 flex flex-wrap gap-2">
        <Button type="primary" icon={<DownloadOutlined />} onClick={handleDownloadTemplate}>
          下载模版
        </Button>
        <HasResource resourceCodes={[ResourceCode.settlement.SETTLEMENT_REVENUE_BILL_OPERATE]}>
          <Upload
              accept=".xlsx,.xls"
              showUploadList={false}
              beforeUpload={(file) => {
                handleUpload(file);
                return false;
              }}
              disabled={uploading}
          >
            <Button icon={<UploadOutlined />} loading={uploading}>
              上传明细
            </Button>
          </Upload>
        </HasResource>
        <HasResource resourceCodes={[ResourceCode.settlement.SETTLEMENT_REVENUE_BILL_CHECK]}>
          <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={loading}>
            刷新任务状态
          </Button>
        </HasResource>
      </div>
      <div className="min-h-[200px]">
        <Table
          rowKey="id"
          columns={columns}
          dataSource={tasks}
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条`,
          }}
          scroll={{ x: 1280 }}
        />
      </div>
    </Modal>
  );
};

export default ClientDetailAdjustmentModal;
